import {Component, ElementRef, computed, input, output, signal, viewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {DeviceCredentialSecret} from '../shared/device-credential.models';

/** Which call produced the password on screen. Only the wording differs; the hand-over is identical. */
export type SecretOrigin = 'issued' | 'rotated';

/**
 * The one moment a register password exists (#469, step 7).
 *
 * ### Why this is a dialog with no accidental exit
 *
 * The backend stores a PBKDF2 verifier of the password and nothing else, so the response that issued or
 * rotated the credential is the **only** place the plaintext will ever appear. There is no later read that
 * returns it and no "show it again"; a password closed away is not recoverable, only replaceable by another
 * rotation. That single fact drives every decision here, and it is the same reasoning — and the same shape —
 * as `InviteCodeDialogComponent`:
 *
 * - **Nothing dismisses this by accident.** No backdrop click, no `Escape`, no auto-close after a copy. The
 *   only exit is the explicit button, whose label states what closing costs. A modal that closes on a stray
 *   click is the exact shape that loses an unrecoverable secret. (`RevokeCredentialDialogComponent` is
 *   deliberately the opposite — cancelling a revoke costs nothing — so do not "fix" one to match the other.)
 * - **Both halves are handed over together.** A till needs the *Kennung* in its user-name field and the
 *   *Passwort* in its password field. Showing only the new password on a rotation would be correct and
 *   useless: whoever walks to the register needs the pair.
 * - **The warning names the remedy**, because rotating again is the only one there is.
 * - **Nothing is persisted.** The password lives in this component's input for as long as it is on screen.
 */
@Component({
  selector: 'device-credential-secret-dialog',
  standalone: true,
  imports: [DatePipe],
  template: `
    <!-- No (click) on the overlay: a stray click outside must not destroy an unrecoverable password. -->
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div class="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg
                  sm:rounded-2xl"
           role="dialog" aria-modal="true" aria-labelledby="credential-secret-heading">
        <div class="space-y-4 p-4 sm:p-6">

          <div>
            <h2 id="credential-secret-heading" class="text-xl font-bold tracking-tight text-gray-900">
              {{ heading() }}
            </h2>
            <p class="mt-1 text-sm text-gray-500">{{ subtitle() }}</p>
          </div>

          <div class="rounded-2xl border border-amber-100 bg-amber-50 p-4" role="alert">
            <p class="text-sm font-bold text-amber-800">Das Passwort wird nur jetzt angezeigt</p>
            <p class="mt-1 text-sm text-amber-800">
              CheqMe speichert das Passwort nicht — nur seine Prüfsumme. Es lässt sich später nicht erneut
              anzeigen. Tragen Sie es jetzt an der Kasse ein. Ist es verloren, wechseln Sie das Passwort
              erneut.
            </p>
          </div>

          <div>
            <label for="credential-client-id"
                   class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Kennung (Feld „Benutzername" an der Kasse)
            </label>
            <!-- The button sits UNDER the field, not beside it, at every width. A 29-character client id
                 and a 48-character password are both wider than what is left of the row once a button
                 takes its share, and a value cut off mid-string cannot be read aloud or checked by eye —
                 which is the one thing this dialog exists to allow. -->
            <div class="flex flex-col gap-2">
              <input id="credential-client-id" #clientIdField type="text" readonly
                     [value]="credential().clientId"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-mono
                            text-sm font-semibold tracking-wide text-gray-900 shadow-sm transition-all
                            duration-200 focus:border-theme-primary-500 focus:ring-1
                            focus:ring-theme-primary-500">
              <button type="button" (click)="copyClientId()"
                      class="inline-flex items-center justify-center rounded-2xl border border-gray-200
                             px-4 py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all
                             duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95">
                Kennung kopieren
              </button>
            </div>
            <p class="mt-2 text-xs text-gray-500">
              Die Kennung bleibt bei einem Passwortwechsel gleich.
            </p>
          </div>

          <div>
            <label for="credential-secret" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Passwort (Feld „Passwort" an der Kasse)
            </label>
            <div class="flex flex-col gap-2">
              <!-- type="text", not "password": this dialog exists to SHOW the value. A masked field here
                   would hide the one thing that cannot be looked up again. -->
              <input id="credential-secret" #secretField type="text" readonly [value]="credential().secret"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-mono
                            text-sm font-semibold tracking-wide text-gray-900 shadow-sm transition-all
                            duration-200 focus:border-theme-primary-500 focus:ring-1
                            focus:ring-theme-primary-500">
              <button type="button" (click)="copySecret()"
                      class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600
                             px-5 py-2.5 text-sm font-semibold text-white shadow-lg
                             shadow-theme-primary-100 transition-all duration-200
                             hover:bg-theme-primary-700 hover:shadow-xl active:scale-95">
                Passwort kopieren
              </button>
            </div>
          </div>

          @if (feedback(); as message) {
            <p class="text-sm font-semibold" role="status" aria-live="polite"
               [class.text-green-700]="copied()" [class.text-red-700]="!copied()">{{ message }}</p>
          }

          <p class="text-sm text-gray-500">
            Gültig bis <span
              class="font-semibold text-gray-700">{{ credential().expiration | date: 'dd.MM.yyyy' }}</span>.
            Danach kann sich damit keine weitere Kasse anmelden. Bereits angemeldete Kassen bleiben angemeldet.
          </p>

          <div class="flex justify-end border-t border-gray-100 pt-4">
            <button type="button" (click)="acknowledged.emit()"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Ich habe das Passwort gesichert — schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class CredentialSecretDialogComponent {
  /** The issued or rotated credential exactly as the server returned it. The only shape with a plaintext. */
  readonly credential = input.required<DeviceCredentialSecret>();

  /** Whether this password was just created or just replaced. Wording only. */
  readonly origin = input<SecretOrigin>('issued');

  /** The label the administrator gave this credential, for the subtitle. */
  readonly name = input<string>('');

  /** The reader confirmed they have the password. The parent closes the dialog. */
  readonly acknowledged = output<void>();

  private readonly clientIdField = viewChild<ElementRef<HTMLInputElement>>('clientIdField');
  private readonly secretField = viewChild<ElementRef<HTMLInputElement>>('secretField');

  protected readonly feedback = signal<string | null>(null);
  protected readonly copied = signal(false);

  protected readonly heading = computed(() =>
    this.origin() === 'rotated' ? 'Neues Passwort für die Kasse' : 'Zugangsdaten für die Kasse');

  protected readonly subtitle = computed(() => {
    const label = this.name().trim();
    return this.origin() === 'rotated'
      ? (label ? 'Das bisherige Passwort von „' + label + '" gilt nicht mehr.'
        : 'Das bisherige Passwort gilt nicht mehr.')
      : (label ? 'Zugangsdaten „' + label + '"' : 'Neue Zugangsdaten');
  });

  protected copyClientId(): void {
    this.copy(this.credential().clientId, this.clientIdField(), 'Kennung kopiert.');
  }

  protected copySecret(): void {
    this.copy(this.credential().secret, this.secretField(), 'Passwort kopiert.');
  }

  /**
   * Copies through the async clipboard API and, when that is unavailable or refused, **selects the text and
   * says so** rather than failing silently. A copy button that quietly does nothing is worse than no button:
   * the reader believes they have the password, closes the dialog, and it is gone. (Same reasoning, and the
   * same shape, as `InviteCodeDialogComponent.copy`.)
   */
  private copy(value: string, field: ElementRef<HTMLInputElement> | undefined, success: string): void {
    const select = () => {
      field?.nativeElement.focus();
      field?.nativeElement.select();
    };

    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) {
      select();
      this.copied.set(false);
      this.feedback.set('Kopieren ist in diesem Browser nicht möglich — der Text ist markiert, bitte mit '
        + 'Strg/Cmd + C kopieren.');
      return;
    }

    clipboard.writeText(value).then(
      () => {
        this.copied.set(true);
        this.feedback.set(success);
      },
      () => {
        select();
        this.copied.set(false);
        this.feedback.set('Kopieren wurde vom Browser abgelehnt — der Text ist markiert, bitte mit '
          + 'Strg/Cmd + C kopieren.');
      });
  }
}
